import React, { ReactNode, Suspense, useCallback, useEffect } from 'react';
import graphql from 'babel-plugin-relay/macro';
import fill from 'lodash/fill';
import { useLazyLoadQuery } from 'react-relay/hooks';
import styled, { css, useTheme } from 'styled-components';
import { ErrorBoundary, formatNZD, useSegment } from '@sparknz/journeys-common';
import {
  ActionLayer,
  Box,
  Button,
  Divider,
  Text,
} from '@sparknz/spark-pattern-library';
import { LinkRegion, trackCustomLink } from '@sparknz/tracking';

import {
  SelectedPlansActionLayerQuery,
  SelectedPlansActionLayerQuery$data,
} from '__generated__/SelectedPlansActionLayerQuery.graphql';
import useAddBundles from 'components/shop/MobilePlans/hooks/useAddBundles';
import ContentLayerLoader from 'shared/components/ContentLayerLoader';
import { ACTION_LAYER_ELEMENT_ID } from 'shared/constants/elementSelector.constants';
import { INFO_LAYER_CONTENT_QUERY_KEY } from 'shared/constants/query.constants';
import useQuery from 'shared/hooks/useQuery';
import { scrollToContainerTop } from 'shared/utils/scroll.utils';
import { useSelectedPlans } from '../../providers';
import { MobilePlansDetails, SelectedPlan } from '../../typings/plans';
import { buildAddBundlesInputBundles } from '../../utils/buildAddBundlesInputBundles';
import { getOfferIds } from '../../utils/getOfferIds.utils';
import { trackActionLayerViewEvent } from '../../utils/trackActionLayerViewEvent';
import GenericErrorBanner from '../GenericErrorBanner';
import PlanConfiguration from './components/PlanConfiguration';
import SelectedPlansDescription from './components/SelectedPlansDescription';
import SelectedPlanRow, { SelectedPlanRowProps } from './SelectedPlanRow';

export const selectedPlansActionLayerQuery = graphql`
  query SelectedPlansActionLayerQuery(
    $offerIds: [String!]!
    $channel: CartChannel!
  ) {
    mpdPromotions(offerIds: $offerIds, channel: $channel) {
      price {
        basePrice
        basePriceExcludingTax
        effectivePrice
        effectivePriceExcludingTax
        discount {
          appliedValue
          appliedValueExcludingTax
          discountedValue
          discountedValueExcludingTax
        }
      }
      bundles {
        id
        items {
          id
          price {
            basePrice
            basePriceExcludingTax
            effectivePrice
            effectivePriceExcludingTax
            discount {
              appliedValue
              appliedValueExcludingTax
              discountedValue
              discountedValueExcludingTax
            }
          }
          productOfferingId
        }
      }
    }
  }
`;

const Container = styled.div`
  ${({ theme: { spacing } }) => css`
    padding: ${spacing.s7} 0;
  `}
`;

const StyledDivider = styled(Divider)`
  ${({ theme: { spacing } }) => css`
    margin-bottom: ${spacing.s1};
  `}
`;

const StyledButton = styled(Button)`
  ${({ theme: { spacing } }) => css`
    margin-bottom: ${spacing.s2};
    width: 100%;
  `}
`;

interface BuildRowsParameter {
  mpdPromotions: SelectedPlansActionLayerQuery$data['mpdPromotions'];
  selectedPlans: SelectedPlan[];
  isRemoveEnabled?: boolean;
}

function getRowsForSelectedPlans({
  mpdPromotions,
  selectedPlans,
  isRemoveEnabled,
}: BuildRowsParameter): SelectedPlanRowProps[] {
  return selectedPlans.flatMap((selectedPlan) => {
    const mpdPromotionBundle = mpdPromotions.bundles.find(
      (bundle) => bundle.items[0].productOfferingId === selectedPlan.offerId,
    );
    if (!mpdPromotionBundle) {
      return [];
    }

    const {
      basePrice,
      basePriceExcludingTax,
      discount,
      effectivePrice,
      effectivePriceExcludingTax,
    } = mpdPromotionBundle.items[0].price[0];

    const rowTemplate = {
      basePrice,
      basePriceExcludingTax: basePriceExcludingTax ?? null,
      discountPrice: discount?.discountedValue ?? null,
      discountedPriceExcludingTax:
        discount?.discountedValueExcludingTax ?? null,
      discountPercentage: discount?.appliedValue ?? null,
      discountPercentageExcludingTax:
        discount?.appliedValueExcludingTax ?? null,
      effectivePrice,
      effectivePriceExcludingTax: effectivePriceExcludingTax ?? null,
      offerId: selectedPlan.offerId,
      planName: selectedPlan.planName,
      quantity: 1,
      isRemovable: isRemoveEnabled,
    };

    return fill(Array(selectedPlan.quantity), rowTemplate)
      .filter(Boolean)
      .map((row, index) => ({
        ...row,
        key: `${row.offerId}-${index}`,
      }));
  });
}

interface ExtractTotalValuesReturn {
  discountedValue: number;
  displayPrice: number;
  displayBasePrice: number;
  discountPercentage: number;
}

function extractTotalValues(
  mpdPromotions: SelectedPlansActionLayerQuery$data['mpdPromotions'],
  isBusiness: boolean,
): ExtractTotalValuesReturn {
  if (!mpdPromotions?.price?.length) {
    return {
      discountedValue: 0,
      displayPrice: 0,
      displayBasePrice: 0,
      discountPercentage: 0,
    };
  }

  const {
    discount,
    effectivePrice,
    effectivePriceExcludingTax,
    basePrice,
    basePriceExcludingTax,
  } = mpdPromotions?.price[0];

  const displayBasePrice = isBusiness ? basePriceExcludingTax : basePrice;

  const displayPrice = isBusiness ? effectivePriceExcludingTax : effectivePrice;

  if (!(displayBasePrice && displayPrice)) {
    throw new Error(
      'Missing tax exclusive display prices when extracting totals',
    );
  }

  if (!discount) {
    return {
      discountedValue: 0,
      displayPrice,
      displayBasePrice,
      discountPercentage: 0,
    };
  }

  const discountPercentage =
    (isBusiness
      ? discount?.appliedValueExcludingTax
      : discount?.appliedValue) ?? 0;

  const discountedValue =
    (isBusiness
      ? discount?.discountedValueExcludingTax
      : discount?.discountedValue) ?? 0;

  return {
    discountedValue,
    displayPrice,
    displayBasePrice,
    discountPercentage,
  };
}

interface SelectedPlansProps {
  planCardsProducts: MobilePlansDetails;
  onClose?: () => void;
  hasTitle?: boolean;
  hasDescription?: boolean;
  isRemoveEnabled?: boolean;
  actions?: ReactNode;
}

export function SelectedPlans({
  planCardsProducts,
  onClose,
  hasTitle = false,
  hasDescription = false,
  isRemoveEnabled = false,
  actions,
}: SelectedPlansProps) {
  const {
    palette: { baseColors },
  } = useTheme();

  const { addBundles, isAddingBundles } = useAddBundles();

  const { selectedPlans } = useSelectedPlans();
  const { channel, isBusiness } = useSegment();

  const { mpdPromotions } = useLazyLoadQuery<SelectedPlansActionLayerQuery>(
    selectedPlansActionLayerQuery,
    {
      channel,
      offerIds: getOfferIds(selectedPlans),
    },
  );

  const {
    displayBasePrice,
    discountedValue,
    discountPercentage,
    displayPrice,
  } = extractTotalValues(mpdPromotions, isBusiness);

  if (!selectedPlans.length) {
    return (
      <Container data-testid="mpd-plan-selecion-view">
        <Text variant="bodySMedium" mb="s6">
          You have no selected plans.
        </Text>
        <LinkRegion regionId="selectedPlansActionLayer">
          <StyledButton variant="default" onClick={onClose}>
            Continue shopping
          </StyledButton>
        </LinkRegion>
      </Container>
    );
  }

  const handleAddCartClick = async () => {
    trackCustomLink('Add to cart');
    const bundles = buildAddBundlesInputBundles({ selectedPlans });
    await addBundles({ bundles, planCardsProducts });
  };

  const selectedPlanRows = getRowsForSelectedPlans({
    mpdPromotions,
    selectedPlans,
    isRemoveEnabled,
  });
  const discountText = `(${discountPercentage}%) ${formatNZD(discountedValue)}`;
  const wildcardSymbol = isBusiness ? <sup>*</sup> : null;
  const hasTeamUpEligiblePlans = selectedPlans.some(
    (plan) => plan.isTeamUpEligible,
  );

  return (
    <>
      {hasTitle ? (
        <Text variant="subheadingM" mb="s3">
          Selection summary
        </Text>
      ) : null}

      {hasDescription ? (
        <SelectedPlansDescription
          hasTeamUpEligiblePlans={hasTeamUpEligiblePlans}
        />
      ) : null}

      {selectedPlanRows.map((selectedPlanRow) => (
        <SelectedPlanRow {...selectedPlanRow} />
      ))}

      <StyledDivider />
      <Box flexDirection="row" justifyContent="space-between" mb="s1">
        <Text variant="bodySDemi">Total</Text>
        <Text variant="bodySDemi">
          {formatNZD(displayPrice)}
          {wildcardSymbol}
        </Text>
      </Box>
      {discountPercentage ? (
        <Box flexDirection="row" justifyContent="space-between" mb="s1">
          <Text variant="bodySDemi" />
          <Text variant="bodySMedium" color={baseColors.mawheranui}>
            <s>
              {formatNZD(displayBasePrice)}
              {wildcardSymbol}
            </s>
          </Text>
        </Box>
      ) : null}
      <Box flexDirection="row" justifyContent="space-between" mb="s1" />

      {hasTeamUpEligiblePlans ? (
        <Box flexDirection="row" justifyContent="space-between" mb="s3">
          <Text variant="bodySDemi">Savings</Text>
          <Text variant="bodySDemi" color={baseColors.mawhero}>
            {discountText}
            {wildcardSymbol}
          </Text>
        </Box>
      ) : null}

      {isBusiness ? (
        <Text variant="bodySDemi" color={baseColors.mawheranui} mb="s4">
          *Prices exclude GST
        </Text>
      ) : null}
      <LinkRegion regionId="selectedPlansActionLayer">
        {actions ? (
          <>{actions}</>
        ) : (
          <>
            <StyledButton
              variant="primary"
              onClick={handleAddCartClick}
              isLoading={isAddingBundles}
            >
              Add to cart
            </StyledButton>
            <StyledButton variant="default" onClick={onClose}>
              Continue shopping
            </StyledButton>
          </>
        )}
      </LinkRegion>
    </>
  );
}

interface SelectedPlansEmptyScreenProps {
  onClose: () => void;
}

function SelectedPlansEmptyScreen({ onClose }: SelectedPlansEmptyScreenProps) {
  return (
    <>
      <Text variant="bodySMedium" mb="s6">
        You have no selected plans.
      </Text>
      <LinkRegion regionId="selectedPlansActionLayer">
        <StyledButton variant="default" onClick={onClose}>
          Continue shopping
        </StyledButton>
      </LinkRegion>
    </>
  );
}

interface SelectedPlansActionLayerContentProps
  extends Omit<SelectedPlansActionLayerProps, 'isOpen'> {
  selectedPlans: SelectedPlan[];
  setLayerTitle: (title: string) => void;
  scrollToTop: () => void;
}

function SelectedPlansActionLayerContent({
  selectedPlans,
  onClose,
  planCardsProducts,
  isMultiPlanEnabled,
  setLayerTitle,
  scrollToTop,
}: SelectedPlansActionLayerContentProps) {
  if (!selectedPlans.length) {
    return <SelectedPlansEmptyScreen onClose={onClose} />;
  }

  return isMultiPlanEnabled ? (
    <SelectedPlans
      onClose={onClose}
      planCardsProducts={planCardsProducts}
      hasDescription
      isRemoveEnabled
    />
  ) : (
    <PlanConfiguration
      setLayerTitle={setLayerTitle}
      selectedPlan={
        selectedPlans.find(({ isPrimary }) => isPrimary) || selectedPlans[0]
      }
      planCardsProducts={planCardsProducts}
      onCancel={onClose}
      scrollToTop={scrollToTop}
    />
  );
}

interface SelectedPlansActionLayerProps {
  isOpen: boolean;
  onClose: () => void;
  planCardsProducts: MobilePlansDetails;
  isMultiPlanEnabled?: boolean;
}

export default function SelectedPlansActionLayer({
  isOpen,
  onClose,
  planCardsProducts,
  isMultiPlanEnabled = true,
}: SelectedPlansActionLayerProps) {
  const layerContainerRef = React.useRef<HTMLDivElement>(null);
  const [layerTitle, setLayerTitle] = React.useState(
    `Selected ${isMultiPlanEnabled ? 'plans' : 'plan'}`,
  );
  const { selectedPlans } = useSelectedPlans();
  const { isBusiness } = useSegment();
  const { query, replaceQuery } = useQuery();

  const filteredPlanCards = selectedPlans?.flatMap((item) => {
    return planCardsProducts.filter(
      (plan) => plan.planDetails.planId === item.offerId,
    );
  });

  useEffect(() => {
    if (isOpen && isMultiPlanEnabled) {
      trackActionLayerViewEvent({
        planDetails: filteredPlanCards,
        isBusiness,
      });
    }
  }, [filteredPlanCards, isBusiness, isOpen, isMultiPlanEnabled]);

  const handleClose = () => {
    trackCustomLink('Continue shopping');
    onClose();
    replaceQuery({ ...query, [INFO_LAYER_CONTENT_QUERY_KEY]: null });
  };

  const scrollToTop = useCallback(() => {
    if (layerContainerRef.current) {
      const dialog = layerContainerRef.current.closest('[role="dialog"]');
      if (dialog) {
        scrollToContainerTop(dialog);
      }
    }
  }, []);

  const handleOnSetLayerTitle = useCallback((title: string) => {
    setLayerTitle(title);
  }, []);

  return (
    <ActionLayer
      isOpen={isOpen}
      title={layerTitle}
      onClose={handleClose}
      status="default"
    >
      <Container
        id={ACTION_LAYER_ELEMENT_ID}
        data-testid="selected-plans-view"
        ref={layerContainerRef}
      >
        <ErrorBoundary fallback={() => <GenericErrorBanner />}>
          <Suspense fallback={<ContentLayerLoader />}>
            <SelectedPlansActionLayerContent
              setLayerTitle={handleOnSetLayerTitle}
              onClose={handleClose}
              planCardsProducts={filteredPlanCards}
              scrollToTop={scrollToTop}
              isMultiPlanEnabled={isMultiPlanEnabled}
              selectedPlans={selectedPlans}
            />
          </Suspense>
        </ErrorBoundary>
      </Container>
    </ActionLayer>
  );
}
